import { Component } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, tap, concatMap } from 'rxjs';
import { ConfirmationDialogActionComponent } from '../../../components/utility/confirmation-dialog-action/confirmation-dialog-action.component';
import { CheckboxInputComponent } from '../../../components/utility/form/checkbox-input/checkbox-input.component';
import { InputLabelComponent } from '../../../components/utility/form/input-label/input-label.component';
import { TextInputComponent } from '../../../components/utility/form/text-input/text-input.component';
import { UploadInputComponent } from '../../../components/utility/form/upload-input/upload-input.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { pageSeoInfo } from '../../../constants/page-seo-info';
import { regex } from '../../../constants/regex';
import { routes } from '../../../constants/routes';
import { FileObject } from '../../../models/file-object.model';
import { UserProfileDetail } from '../../../models/user-profile.model';
import { ApiService } from '../../../services/api.service';
import { AuthService } from '../../../services/auth.service';
import { SeoService } from '../../../services/seo.service';
import { ToasterService } from '../../../services/toaster.service';
import { UtilityService } from '../../../services/utility.service';

@Component({
  selector: 'app-user-identity-setup',
  standalone: true,
  imports: [
    TextInputComponent,
    ReactiveFormsModule,
    InputLabelComponent,
    CheckboxInputComponent,
    UploadInputComponent,
  ],
  templateUrl: './user-identity-setup.component.html',
  styleUrl: './user-identity-setup.component.scss',
})
export class UserIdentitySetupComponent {
  public myProfileForm!: FormGroup;
  resetInput = false;

  isEmailVerifiable: boolean = false;
  isEmailVerified: string = '';
  verifiedEmailValue: string = '';

  private updateUserDataSub!: Subscription;

  public profileImage?: string = '';
  public isUserNameVerified: string | boolean = '';
  private imageProfileData?: FileObject;
  private userDetails!: UserProfileDetail;

  readonly profileFormValidations = {
    name: {
      maxLength: 50,
    },
    mobileNo: {
      maxLength: 10,
    },
    email: {
      maxLength: 150,
    },
  };

  readonly userNameKeypressFn = () => {
    this.isUserNameVerified = false;
    return true;
  };

  verificationKey: string = '';

  emailAlreadyVerifiedObj: {
    [k: string]: any;
  } = {
    email: '',
    isEmailVerifiable: true,
    authKey: '',
  };

  constructor(
    private router: Router,
    private matDialog: MatDialog,
    public toaster: ToasterService,
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private seoService: SeoService,
    public utilityService: UtilityService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state;
    this.emailAlreadyVerifiedObj = state;
    if (this.emailAlreadyVerifiedObj?.['authKey']) {
      this.isEmailVerifiable = false;
      this.isEmailVerified = '/images/success.svg';
      this.verifiedEmailValue = this.emailAlreadyVerifiedObj?.['email'];
      this.verificationKey = this.emailAlreadyVerifiedObj?.['authKey'];
      this.myProfileForm?.get('email')?.setValue(this.verifiedEmailValue);
    }
  }

  ngOnInit() {
    this.initProfileForm();
    const { title, description, canonicalUrl } = pageSeoInfo['profile'];
    this.seoService.setPageTitle(title);
    this.seoService.setPageDescription(description);
    this.seoService.setCanonicalUrl(canonicalUrl);
  }

  private initProfileForm() {
    this.myProfileForm = new FormGroup({
      username: new FormControl('', [
        Validators.pattern(regex.userNameWithNoSpecialChar),
        Validators.required,
        Validators.minLength(6),
      ]),
      fullName: new FormControl('', [Validators.required]),
      profilePicture: new FormControl(''),
      phone: new FormControl('', [
        Validators.required,
        Validators.pattern(regex.mobileNumber),
      ]),
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        Validators.pattern(regex.email),
      ]),

      isAgreeTnc: new FormControl('', [Validators.requiredTrue]),
    });
    this.myProfileForm?.get('email')?.setValue(this.verifiedEmailValue);
  }

  onProfileUpload(imageProfileData: FileObject) {
    this.resetInput = false;
    this.imageProfileData = Array.isArray(imageProfileData)
      ? imageProfileData[0]
      : imageProfileData;
    this.profileImage = this.imageProfileData.base64;
  }

  onProfileImageRemove() {
    this.imageProfileData = undefined;
    this.profileImage = undefined;
    this.resetInput = true;
    this.myProfileForm.get('profilePicture')?.reset();
  }

  onInputBlur(controlName: 'email' | 'username') {
    const control = this.myProfileForm.get(controlName);

    if (control?.invalid) {
      this.updateVerificationStatus(controlName, false);
      return;
    }

    if (controlName === 'email') {
      this.isEmailVerifiable = false;
      this.checkIfExist(control.value, controlName);
    } else if (controlName === 'username') {
      this.checkIfExist(control.value, controlName);
      if (this.userDetails?.username === control.value) {
        this.isUserNameVerified = '/images/success.svg';
        return;
      } else {
        this.isUserNameVerified = '';
      }
    }
  }

  checkIfExist(value: string, controlName: 'email' | 'username') {
    this.apiService
      .checkUsernameAndEmailExist(value, controlName)
      .pipe(
        tap((resp) => {
          if (resp) {
            return;
          }
          if (controlName === 'email') {
            if (
              this.userDetails?.email ===
                this.myProfileForm.get(controlName).value ||
              (this.isEmailVerified &&
                this.verifiedEmailValue ===
                  this.myProfileForm.get(controlName).value)
            ) {
              this.isEmailVerified = '/images/success.svg';
            } else {
              this.isEmailVerifiable = true;
              this.isEmailVerified = '';
              this.verifiedEmailValue = '';
            }
          }

          controlName === 'username' && (this.isUserNameVerified = true);
        }),
      )
      .subscribe((resp) => {
        if (resp) {
          const existingErrors =
            this.myProfileForm.get(controlName).errors || {};
          this.myProfileForm
            .get(controlName)
            .setErrors({ ...existingErrors, isExist: true });
          if (controlName === 'email') {
            if (this.myProfileForm.get('email').errors?.['isExist']) return;
          }
          return;
        }
      });
  }

  onPrivacyPolicyClick(event: any) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/${routes.home}/${routes.privacyPolicy}`]),
    );

    this.utilityService.openUrlInNewTab(url);
  }

  onTermsOfServiceClick(event: any) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([`/${routes.home}/${routes.termsNCondition}`]),
    );

    this.utilityService.openUrlInNewTab(url, true);
  }

  onUpdateProfile() {
    const isAgreeInvalid = this.myProfileForm.get('isAgreeTnc')?.invalid;

    const hasOtherFieldsInvalid = Object.keys(this.myProfileForm.controls)
      .filter((key) => key !== 'isAgreeTnc')
      .some((key) => this.myProfileForm.get(key)?.invalid);

    if (hasOtherFieldsInvalid) {
      this.myProfileForm.markAllAsTouched();
      return;
    }

    if (!this.verifiedEmailValue) {
      this.toaster.error('Please verify your email before you proceed');
      return;
    }

    if (isAgreeInvalid) {
      this.toaster.error(
        'You must agree to the Terms of Use and Privacy Policy before proceeding.',
      );
      return;
    }

    const myProfileDetail = this.myProfileForm.getRawValue();
    const bodyObj = {
      email: myProfileDetail.email || '',
      key: this.verificationKey,
      username: myProfileDetail.username || '',
      fullname: myProfileDetail.fullName || '',
      phone: myProfileDetail.phone || '',
    };

    const uploadImageBody = {
      file_name: this.imageProfileData?.fileName || '',
      image_url: this.imageProfileData?.base64 || '',
      type: 'content',
    };

    this.updateUserDataSub = this.apiService
      .uploadImage(uploadImageBody)
      .pipe(
        concatMap((resp) => {
          const submitPayload = {
            ...bodyObj,
            profilePic: encodeURI(resp.url) || '',
          };

          return this.apiService.registerUserViaEmail(submitPayload);
        }),
      )
      .subscribe({
        next: (resp) => {
          this.toaster.success('User registered successfully.');
          this.utilityService.openExternalPage(resp);
        },
        error: apiErrorHandler.bind(this),
      });
  }

  onVerifyEmail() {
    const controlValue = this.myProfileForm.get('email')?.value;
    if (!controlValue) return;

    this.verifyEmail(controlValue);
  }

  verifyEmail(email: string, isResend: boolean = false) {
    this.apiService
      .sendOtpToEmail(email)
      .pipe()
      .subscribe({
        next: (res) => {
          this.toaster.success(res.message);
          if (!isResend) {
            this.matDialog
              .open(ConfirmationDialogActionComponent, {
                data: {
                  dialogIcon: 'images/verify-otp.svg',
                  iconAlt: 'Verify OTP',
                  iconTitle: 'Verify your email',
                  headerText: 'Verify your email',
                  message: this.sanitizer.bypassSecurityTrustHtml(
                    `<span style = "color : #475467">We've sent a code to <b>${email}</b></span>`,
                  ),
                  primaryButtonClass: 'btn-secondary',
                  primaryButtonText: 'Verify',
                  showOTPInput: true,
                  isRemarkMandatory: true,
                  viewAsinnerHTML: true,
                  submit: this.verifyEmail.bind(this),
                  isResendOTP: 'true',
                  isVerifyUserEmail: true,
                  email,
                },
              })
              .afterClosed()
              .subscribe((res) => {
                if (!res) return;
                this.verificationKey = res;
                this.verifiedEmailValue = email;
                this.updateVerificationStatus('email', true);
              });
          }
        },
        error: (error) => {
          this.toaster.error(error.message);
        },
      });
  }

  updateVerificationStatus(
    controlName: 'email' | 'phone' | 'username',
    isVerified: boolean,
  ) {
    if (controlName === 'email') {
      this.isEmailVerifiable = false;
      this.isEmailVerified = isVerified ? '/images/success.svg' : '';
    } else if (controlName === 'username') {
      this.isUserNameVerified = isVerified ? '/images/success.svg' : '';
    }
  }

  redirectToHome() {
    this.router.navigate(['/', routes.home], { replaceUrl: true });
  }

  ngOnDestroy() {
    this.updateUserDataSub?.unsubscribe();
  }
}
